import type {
  LocationCaseSummary,
  LocationDrawerSummary,
  LocationStorageRoomSummary,
  LocationWithoutStorageRoomSummary,
  SampleLocationIndex,
} from '../services/sample.service';
import { formatEntityDisplayName } from './softBreakIdentifier';

/** 選択中のケースセル（保管庫×引き出し×ケース） */
export interface CaseLocationCell {
  storageRoomId: number | null;
  drawerNumber: number | null;
  caseName: string | null;
}

export function caseLocationCellKey(cell: CaseLocationCell): string {
  return [
    cell.storageRoomId ?? 'none',
    cell.drawerNumber ?? 'none',
    cell.caseName ?? 'none',
  ].join('|');
}

type CaseTreeTranslate = (key: string, options?: Record<string, unknown>) => string;

/**
 * ツリー内のケース行ラベル。
 * 引き出し見出しが既に `#N` を出すため、未設定ケースに引き出し番号を重ねない。
 */
export function formatCaseTreeCaseLabel(
  caseName: string | null | undefined,
  t: CaseTreeTranslate
): string {
  if (caseName) return formatEntityDisplayName(caseName, t);
  return t('sample.case_manager.unassigned_case');
}

/**
 * ツリー保管庫メタ行の場所表示。
 * 長い組織名プレフィックスは省略し、識別しやすい末尾（部屋名など）を優先。
 * フル文字列は title / aria 用に別途渡す。
 */
const CASE_TREE_LOCATION_ORG_PREFIXES = [
  '国立感染症研究所',
  'National Institute of Infectious Diseases',
] as const;

/** 表示用: 末尾の部屋番号 `（4）` / `(4)` を ` · 4` に（full は保存値のまま） */
function formatLocationRoomNumberJoiner(text: string): string {
  return text.replace(/[（(]\s*(\d+)\s*[）)]/gu, ' · $1');
}

export function formatCaseTreeLocationDisplay(
  location: string | null | undefined
): { display: string; full: string } {
  const full = location?.trim() || '';
  if (!full) return { display: '', full: '' };
  for (const prefix of CASE_TREE_LOCATION_ORG_PREFIXES) {
    if (full === prefix) return { display: full, full };
    if (full.startsWith(prefix)) {
      const rest = full.slice(prefix.length).replace(/^[\s·,，\-－／/]+/u, '');
      if (rest.length >= 2) {
        return { display: formatLocationRoomNumberJoiner(rest), full };
      }
    }
  }
  return { display: formatLocationRoomNumberJoiner(full), full };
}

/** ケース名検索用（表示ラベル＋引き出し文脈） */
export function caseTreeCaseSearchHaystack(
  caseName: string | null | undefined,
  drawerNumber: number | null,
  t: CaseTreeTranslate
): string {
  const label = formatCaseTreeCaseLabel(caseName, t);
  const drawer =
    drawerNumber != null
      ? `#${drawerNumber}`
      : t('sample.case_manager.unassigned_drawer');
  return `${label} ${drawer}`;
}

/**
 * 右ペイン見出し。
 * compact: 保管庫名を省略（md+ でツリーが並ぶとき）。引き出し＋ケースの二重「未設定」は1語にまとめる。
 * includeRoom: compact でも保管庫を末尾に付ける（xs でツリーが隠れる master-detail 用）。
 */
export function formatCasePanelLocationHeader(
  cell: CaseLocationCell,
  t: CaseTreeTranslate,
  options?: { roomName?: string | null; compact?: boolean; includeRoom?: boolean }
): string {
  const compact = options?.compact === true;
  const caseLabel = formatCaseTreeCaseLabel(cell.caseName, t);
  const drawerLabel =
    cell.drawerNumber != null
      ? `#${cell.drawerNumber}`
      : t('sample.case_manager.unassigned_drawer');
  const bothUnassigned = cell.drawerNumber == null && cell.caseName == null;
  const unassignedBoth = t('sample.case_manager.unassigned_drawer_and_case', {
    defaultValue: '引き出し · ケース未設定',
  });
  /** フル見出しはツリー順（引き出し → ケース） */
  const drawerCaseTree = bothUnassigned
    ? unassignedBoth
    : `${drawerLabel} · ${caseLabel}`;
  /**
   * compact（狭い右ペイン）: ケース名があるときは先頭に置き、
   * text-truncate で切れても識別しやすい語が残るようにする（旧: `#0 · Box-…`）。
   */
  const drawerCaseCompact = bothUnassigned
    ? unassignedBoth
    : cell.caseName != null
      ? `${caseLabel} · ${drawerLabel}`
      : `${drawerLabel} · ${caseLabel}`;

  const roomPart =
    cell.storageRoomId == null
      ? t('sample.case_manager.unassigned_room')
      : options?.roomName
        ? formatEntityDisplayName(options.roomName, t)
        : `#${cell.storageRoomId}`;

  if (compact) {
    // xs master-detail: 保管庫を先頭に（text-truncate でも部屋文脈が残る）。ケース名は続く
    return options?.includeRoom ? `${roomPart} · ${drawerCaseCompact}` : drawerCaseCompact;
  }

  return `${roomPart} · ${drawerCaseTree}`;
}

type IndexDrawer = {
  drawerNumber: number | null;
  cases: Array<{ caseName: string | null; sampleCount: number }>;
};

/** ロケーション索引上のセル件数（右ペイン読込中のヘッダ表示用）。無ければ null */
export function getCaseLocationSampleCount(
  index: SampleLocationIndex,
  cell: CaseLocationCell
): number | null {
  const drawers =
    cell.storageRoomId == null
      ? index.withoutStorageRoom?.drawers
      : index.storageRooms.find((r) => r.id === cell.storageRoomId)?.drawers;
  if (!drawers) return null;
  const drawer = drawers.find((d) => d.drawerNumber === cell.drawerNumber);
  if (!drawer) return null;
  const hit = drawer.cases.find((c) => c.caseName === cell.caseName);
  return hit ? hit.sampleCount : null;
}

function walkDrawersForCaseCell(
  storageRoomId: number | null,
  drawers: IndexDrawer[],
  preferPopulated: boolean
): CaseLocationCell | null {
  let fallback: CaseLocationCell | null = null;
  for (const drawer of drawers) {
    for (const c of drawer.cases) {
      const cell: CaseLocationCell = {
        storageRoomId,
        drawerNumber: drawer.drawerNumber,
        caseName: c.caseName,
      };
      if (!fallback) fallback = cell;
      if (!preferPopulated || c.sampleCount > 0) return cell;
    }
  }
  return preferPopulated ? null : fallback;
}

/** 指定保管庫内の先頭ケース（既定は件数>0優先。空庫は最初のケースへフォールバック） */
export function findFirstCaseCellInRoom(
  index: SampleLocationIndex,
  storageRoomId: number,
  options?: { preferPopulated?: boolean }
): CaseLocationCell | null {
  const room = index.storageRooms.find((r) => r.id === storageRoomId);
  if (!room) return null;
  const preferPopulated = options?.preferPopulated !== false;
  return (
    walkDrawersForCaseCell(storageRoomId, room.drawers, preferPopulated) ??
    walkDrawersForCaseCell(storageRoomId, room.drawers, false)
  );
}

/** 件数>0 の最初のケースセル（初期表示用）。無ければ null */
export function findFirstPopulatedCaseCell(
  index: SampleLocationIndex
): CaseLocationCell | null {
  for (const room of index.storageRooms) {
    if (room.sampleCount <= 0) continue;
    const hit = walkDrawersForCaseCell(room.id, room.drawers, true);
    if (hit) return hit;
  }
  if (index.withoutStorageRoom && index.withoutStorageRoom.sampleCount > 0) {
    return walkDrawersForCaseCell(null, index.withoutStorageRoom.drawers, true);
  }
  return null;
}

/**
 * ケース管理の初期セル。
 * pending（一覧／保管室からの遷移）を優先。無ければデスクトップのみ先頭ケースを自動選択し、
 * モバイルはツリー選択を促すため null。
 */
export function resolveInitialCaseManagerCell(
  index: SampleLocationIndex,
  pending: CaseLocationCell | null,
  options: { autoSelectFirst: boolean }
): CaseLocationCell | null {
  if (pending) {
    const resolved = resolveCaseLocationPendingAgainstIndex(index, pending);
    if (resolved) return resolved;
  }
  if (options.autoSelectFirst) return findFirstPopulatedCaseCell(index);
  return null;
}

/**
 * Router state の pending セルを索引に解決する。
 * 保管庫だけ指定（drawer/case なし）なら、その庫内の先頭ケースへ落とす。
 */
export function resolveCaseLocationPendingAgainstIndex(
  index: SampleLocationIndex,
  pending: CaseLocationCell
): CaseLocationCell | null {
  if (isCellInLocationIndex(index, pending)) return pending;
  if (
    pending.storageRoomId != null &&
    pending.drawerNumber == null &&
    pending.caseName == null
  ) {
    return findFirstCaseCellInRoom(index, pending.storageRoomId);
  }
  return null;
}

/** 旧 URL クエリ（?storageRoomId=&drawerNumber=&caseName=）の除去用 */
export function hasLegacySampleListLocationSearchParams(params: URLSearchParams): boolean {
  return (
    params.has('storageRoomId') || params.has('drawerNumber') || params.has('caseName')
  );
}

export function stripLegacySampleListLocationSearchParams(
  params: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('storageRoomId');
  next.delete('drawerNumber');
  next.delete('caseName');
  return next;
}

export function parseLegacySampleListLocationFromSearchParams(
  params: URLSearchParams
): CaseLocationCell | null {
  const storageRoomIdParam = params.get('storageRoomId');
  const drawerNumberParam = params.get('drawerNumber');
  const caseNameParam = params.get('caseName');
  if (!storageRoomIdParam && !drawerNumberParam && !caseNameParam) return null;

  let storageRoomId: number | null = null;
  if (storageRoomIdParam) {
    const n = parseInt(storageRoomIdParam, 10);
    if (Number.isNaN(n)) return null;
    storageRoomId = n;
  }

  let drawerNumber: number | null = null;
  if (drawerNumberParam) {
    const n = parseInt(drawerNumberParam, 10);
    if (Number.isNaN(n)) return null;
    drawerNumber = n;
  }

  const caseName = caseNameParam?.trim() ? caseNameParam : null;
  return { storageRoomId, drawerNumber, caseName };
}

export function caseLocationCellToSampleListFilters(
  cell: CaseLocationCell
): { storageRoomId?: number; drawerNumber?: string; caseName?: string } {
  return {
    ...(cell.storageRoomId != null ? { storageRoomId: cell.storageRoomId } : {}),
    ...(cell.drawerNumber != null ? { drawerNumber: String(cell.drawerNumber) } : {}),
    ...(cell.caseName != null ? { caseName: cell.caseName } : {}),
  };
}

/** 旧 URL クエリ（?room=&drawer=&case=）の除去用 */
export function hasLegacyCaseLocationSearchParams(params: URLSearchParams): boolean {
  return params.has('room') || params.has('drawer') || params.has('case');
}

export function stripLegacyCaseLocationSearchParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('room');
  next.delete('drawer');
  next.delete('case');
  return next;
}

/** DataTables 等: ケース管理リンク用 data 属性（Router state 引き渡し用） */
export function caseLocationCellHtmlDataAttrs(cell: CaseLocationCell): string {
  const room = cell.storageRoomId == null ? 'none' : String(cell.storageRoomId);
  const drawer = cell.drawerNumber == null ? 'none' : String(cell.drawerNumber);
  const caseVal =
    cell.caseName == null ? 'none' : encodeURIComponent(cell.caseName);
  return `data-case-room="${room}" data-case-drawer="${drawer}" data-case-name="${caseVal}"`;
}

export function parseCaseLocationFromNavElement(el: HTMLElement): CaseLocationCell | null {
  const roomRaw = el.dataset.caseRoom;
  if (roomRaw == null) return null;
  let storageRoomId: number | null;
  if (roomRaw === 'none') {
    storageRoomId = null;
  } else {
    const n = parseInt(roomRaw, 10);
    if (Number.isNaN(n)) return null;
    storageRoomId = n;
  }
  const drawerRaw = el.dataset.caseDrawer ?? 'none';
  let drawerNumber: number | null;
  if (drawerRaw === 'none') {
    drawerNumber = null;
  } else {
    const n = parseInt(drawerRaw, 10);
    if (Number.isNaN(n)) return null;
    drawerNumber = n;
  }
  const caseRaw = el.dataset.caseName ?? 'none';
  const caseName = caseRaw === 'none' ? null : decodeURIComponent(caseRaw);
  return { storageRoomId, drawerNumber, caseName };
}

export function parseCaseLocationFromSearchParams(
  params: URLSearchParams
): CaseLocationCell | null {
  const roomRaw = params.get('room');
  if (roomRaw === null) return null;

  const storageRoomId = roomRaw === 'none' ? null : parseInt(roomRaw, 10);
  if (roomRaw !== 'none' && Number.isNaN(storageRoomId)) return null;

  const drawerRaw = params.get('drawer');
  let drawerNumber: number | null = null;
  if (drawerRaw !== null) {
    if (drawerRaw === 'none') {
      drawerNumber = null;
    } else {
      const n = parseInt(drawerRaw, 10);
      if (Number.isNaN(n)) return null;
      drawerNumber = n;
    }
  }

  const caseRaw = params.get('case');
  let caseName: string | null = null;
  if (caseRaw !== null) {
    caseName = caseRaw === 'none' ? null : caseRaw;
  }

  return { storageRoomId, drawerNumber, caseName };
}

export function caseLocationToSearchParams(cell: CaseLocationCell): URLSearchParams {
  const p = new URLSearchParams();
  p.set('room', cell.storageRoomId == null ? 'none' : String(cell.storageRoomId));
  p.set('drawer', cell.drawerNumber == null ? 'none' : String(cell.drawerNumber));
  p.set('case', cell.caseName == null ? 'none' : cell.caseName);
  return p;
}

function normalizeCaseNameForCell(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

/** location-index に存在するセルのみ URL・API で許可 */
export function isCellInLocationIndex(
  index: SampleLocationIndex,
  cell: CaseLocationCell
): boolean {
  const caseName = normalizeCaseNameForCell(cell.caseName);

  const findInDrawers = (
    drawers: SampleLocationIndex['storageRooms'][0]['drawers']
  ): boolean => {
    const drawer = drawers.find((d) => d.drawerNumber === cell.drawerNumber);
    if (!drawer) return false;
    return drawer.cases.some((c) => c.caseName === caseName);
  };

  if (cell.storageRoomId == null) {
    if (!index.withoutStorageRoom) return false;
    return findInDrawers(index.withoutStorageRoom.drawers);
  }

  const room = index.storageRooms.find((r) => r.id === cell.storageRoomId);
  if (!room) return false;
  return findInDrawers(room.drawers);
}

export function buildSampleListParamsForCell(
  cell: CaseLocationCell,
  options?: { adminViewAll?: boolean }
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    limit: 1000,
    page: 1,
  };
  if (options?.adminViewAll !== true) {
    params.includeSharedStorageSamples = true;
  } else {
    params.adminViewAll = true;
  }
  if (cell.storageRoomId == null) {
    params.storageRoomIdIsNull = true;
  } else {
    params.storageRoomId = cell.storageRoomId;
  }
  if (cell.drawerNumber == null) {
    params.drawerNumberIsNull = true;
  } else {
    params.drawerNumberExact = cell.drawerNumber;
  }
  if (cell.caseName == null) {
    params.caseNameIsNull = true;
  } else {
    params.caseNameExact = cell.caseName;
  }
  return params;
}

export function buildSampleManagerHref(_cell?: CaseLocationCell): string {
  return '/samples';
}

export function buildCaseManagerHref(_cell?: CaseLocationCell): string {
  return '/samples/cases';
}

export function sampleToCaseLocationCell(sample: {
  storageRoomId?: number;
  storageRoom?: { id: number } | null;
  drawerNumber?: number | null;
  caseName?: string | null;
}): CaseLocationCell {
  const rawCase = sample.caseName?.trim();
  return {
    storageRoomId: sample.storageRoom?.id ?? sample.storageRoomId ?? null,
    drawerNumber: sample.drawerNumber ?? null,
    caseName: rawCase ? rawCase : null,
  };
}

type StorageRoomLabelSource = { name?: string; location?: string | null } | null | undefined;

export type SampleStorageLabels = {
  /** title / aria 用の全文 */
  full: string;
  /** 1行（ソート・フォールバック）。保管庫名は落とさない */
  compact: string;
  /** セル1行目: 保管庫名 */
  roomLine: string;
  /** セル2行目: #引き出し · ケース（無ければ空） */
  detailLine: string;
};

/** 一覧の保管列用。セルは roomLine + detailLine の2段、省略は CSS */
export function formatSampleStorageLabels(
  cell: CaseLocationCell,
  room: StorageRoomLabelSource,
  t: (key: string, options?: Record<string, unknown>) => string
): SampleStorageLabels {
  const unassignedRoom = t('sample.case_manager.unassigned_room');
  const roomNameRaw = room?.name?.trim();
  const roomName = roomNameRaw ? formatEntityDisplayName(roomNameRaw, t) : roomNameRaw;
  const caseName = cell.caseName ? formatEntityDisplayName(cell.caseName, t) : cell.caseName;
  const roomFull = roomName
    ? `${roomName}${room?.location ? ` · ${room.location}` : ''}`
    : cell.storageRoomId == null
      ? unassignedRoom
      : '-';
  const roomLine = roomName || (cell.storageRoomId == null ? unassignedRoom : '-');

  const fullParts = [roomFull];
  if (cell.drawerNumber != null) {
    fullParts.push(`#${cell.drawerNumber}`);
  }
  if (caseName) {
    fullParts.push(`${t('sample.case')} · ${caseName}`);
  }

  // 引き出しは #n、ケースは名前のみ（帳票モバイル facts と同型でスキャンしやすい）
  const detailParts: string[] = [];
  if (cell.drawerNumber != null) {
    detailParts.push(`#${cell.drawerNumber}`);
  }
  if (caseName) {
    detailParts.push(caseName);
  }
  const detailLine = detailParts.join(' · ');

  const compact =
    roomLine && roomLine !== '-'
      ? detailLine
        ? `${roomLine} · ${detailLine}`
        : roomLine
      : detailLine || roomLine;

  return {
    full: fullParts.join(' · '),
    compact,
    roomLine,
    detailLine,
  };
}

export type LocationTreeData = SampleLocationIndex;

export type { LocationCaseSummary, LocationDrawerSummary, LocationStorageRoomSummary, LocationWithoutStorageRoomSummary };
